'use client';

import {
  LazySampleOrder,
  SampleOrderErrorFallback,
  SampleOrderSuspenseFallback,
} from '@packages/sample-order';
import type { LinkButtonProps } from '@packages/shared';
import { Dialog, ErrorBoundary, LinkButton, Stack } from '@packages/shared';
import { Fabricsamples32 } from '@packages/themes/icons';
import { Suspense, useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { SampleOrderLinkFragmentFragmentDoc } from '@packages/gql/generated/shopping/SampleOrderLinkFragmentFragmentDoc';
import { sampleOrderLinkMessages } from './messages';

/* GraphQL */ `
  fragment SampleOrderLinkFragment on Product {
    akl
    sampleOrderType
  }
`;

export type SampleOrderLinkProps = LinkButtonProps & {
  data: FragmentType<typeof SampleOrderLinkFragmentFragmentDoc>;
};

/**
 * This component is used to display a clickable call to action (CtA) link,
 * which opens a dialog that contains the `SampleOrder`.
 * */
export const SampleOrderLink = ({ data, sx, ...rest }: SampleOrderLinkProps) => {
  const { akl, sampleOrderType } = getFragmentData(SampleOrderLinkFragmentFragmentDoc, data);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();

  return (
    <>
      <LinkButton
        {...rest}
        sx={{ ...sx, cursor: 'pointer' }}
        disableUnderlined
        onClick={() => {
          setIsDialogOpen(true);
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Fabricsamples32
            sx={{
              fontSize: '1.5rem',
            }}
          />
          {formatMessage(
            sampleOrderType === 'WOOD'
              ? sampleOrderLinkMessages.WOOD
              : sampleOrderLinkMessages.UPHOLSTERY,
          )}
        </Stack>
      </LinkButton>
      <Dialog
        size="xl"
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        title={formatMessage(sampleOrderLinkMessages.modalHeadline)}
        dialogContentSxProps={{ height: '100%', overflow: 'hidden', flex: '1', display: 'flex' }}
        dialogContent={
          <ErrorBoundary fallback={(reset) => <SampleOrderErrorFallback onCloseHandler={reset} />}>
            <Suspense fallback={<SampleOrderSuspenseFallback />}>
              <LazySampleOrder akl={akl} abortHandler={() => setIsDialogOpen(false)} />
            </Suspense>
          </ErrorBoundary>
        }
      />
    </>
  );
};
